import {
  PROPERTY_TYPE_CATALOG,
  isSubtypeAllowedForType,
  type PropertyTypeCode,
} from '@/types/property.types';

import type { PublicPropertyFilters } from './types';
import { normalizePublicPropertyFilters } from './serializers';

const RENT_ONLY_OPERATION = 'renta';

export const PUBLIC_LISTING_BASE_PATH = '/renta';
export const LEGACY_PUBLIC_LISTING_BASE_PATH = '/propiedades';

const LOCATION_SEGMENT_TO_LABEL: Record<string, string> = {
  cdmx: 'Ciudad de México',
  'ciudad-de-mexico': 'Ciudad de México',
  'mexico-city': 'Ciudad de México',
  'estado-de-mexico': 'Estado de México',
  queretaro: 'Querétaro',
  'santiago-de-queretaro': 'Santiago de Querétaro',
};

const LOCATION_LABEL_TO_SEGMENT: Record<string, string> = {
  cdmx: 'ciudad-de-mexico',
  'ciudad de mexico': 'ciudad-de-mexico',
  'ciudad de méxico': 'ciudad-de-mexico',
  'mexico city': 'ciudad-de-mexico',
  'estado de mexico': 'estado-de-mexico',
  'estado de méxico': 'estado-de-mexico',
  queretaro: 'queretaro',
  querétaro: 'queretaro',
  'santiago de queretaro': 'santiago-de-queretaro',
  'santiago de querétaro': 'santiago-de-queretaro',
};

const PROPERTY_TYPE_SEGMENT_MAP = new Map<string, PropertyTypeCode>(
  (Object.keys(PROPERTY_TYPE_CATALOG) as PropertyTypeCode[]).map((code) => [slugifyListingSegment(code), code])
);

export type PublicPropertiesSeoSegments = {
  propertyType: PropertyTypeCode | null;
  propertySubtype: string | null;
  city: string | null;
  municipality: string | null;
  colonia: string | null;
};

export type PublicPropertiesUrlState = {
  filters: PublicPropertyFilters;
  page: number;
  locationLabel: string | null;
  lat: number | null;
  lng: number | null;
  seoSegments: PublicPropertiesSeoSegments;
  canonicalPath: string;
  canonicalSearch: string;
  canonicalHref: string;
  isCanonical: boolean;
  matchedBasePath: string | null;
  isLegacyBasePath: boolean;
};

type ParsePublicPropertiesInput =
  | URLSearchParams
  | string
  | {
      pathname?: string;
      search?: string;
    };

type BuildPublicPropertiesHrefOptions = {
  filters?: PublicPropertyFilters;
  page?: number;
  locationLabel?: string | null;
  lat?: number | null;
  lng?: number | null;
};

type SeoRouteExtraction = {
  matchedBasePath: string | null;
  isLegacyBasePath: boolean;
  isValid: boolean;
  seoSegments: PublicPropertiesSeoSegments;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const titleCase = (value: string) =>
  value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

function parseInteger(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseFloatNumber(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseBoolean(value: string | null): boolean | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return null;
}

function parseJsonArray(value: string | null): string[] | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return null;
    const cleaned = parsed.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return cleaned.length ? cleaned : null;
  } catch {
    return null;
  }
}

function parseStringArrayParam(params: URLSearchParams, arrayKeys: string[], scalarKeys: string[] = []): string[] | null {
  for (const key of arrayKeys) {
    const parsed = parseJsonArray(params.get(key));
    if (parsed?.length) return parsed;
  }

  for (const key of scalarKeys) {
    const value = params.get(key);
    if (isNonEmptyString(value)) return [value.trim()];
  }

  return null;
}

function firstValue(params: URLSearchParams, keys: string[]): string | null {
  for (const key of keys) {
    const value = params.get(key);
    if (value && value.trim()) return value.trim();
  }
  return null;
}

function normalizeLocationLabel(value: string): string {
  const normalized = value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  const aliased = LOCATION_LABEL_TO_SEGMENT[normalized];
  if (aliased) {
    return LOCATION_SEGMENT_TO_LABEL[aliased] ?? value.trim();
  }

  return value.trim();
}

export function slugifyListingSegment(value: string): string {
  const raw = value.trim();
  if (!raw) return '';

  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  if (LOCATION_LABEL_TO_SEGMENT[normalized]) {
    return LOCATION_LABEL_TO_SEGMENT[normalized];
  }

  return normalized
    .replace(/[_/]+/g, ' ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function humanizeListingSegment(value: string): string {
  const normalized = slugifyListingSegment(value);
  if (!normalized) return '';
  if (LOCATION_SEGMENT_TO_LABEL[normalized]) return LOCATION_SEGMENT_TO_LABEL[normalized];
  return titleCase(normalized.replace(/-/g, ' '));
}

function resolvePropertyTypeSegment(segment: string | undefined): PropertyTypeCode | null {
  if (!segment) return null;
  return PROPERTY_TYPE_SEGMENT_MAP.get(slugifyListingSegment(segment)) ?? null;
}

function resolvePropertySubtypeSegment(
  propertyType: PropertyTypeCode | null,
  segment: string | undefined,
): string | null {
  if (!propertyType || !segment) return null;

  const normalized = slugifyListingSegment(segment).replace(/-/g, '_');
  return isSubtypeAllowedForType(propertyType, normalized) ? normalized : null;
}

function emptySeoSegments(): PublicPropertiesSeoSegments {
  return {
    propertyType: null,
    propertySubtype: null,
    city: null,
    municipality: null,
    colonia: null,
  };
}

function stripListingBasePath(pathname: string): SeoRouteExtraction {
  const cleanPath = (pathname || '').split('?')[0].split('#')[0];
  const segments = cleanPath.split('/').filter(Boolean);
  const base = segments[0] ? `/${segments[0]}` : null;
  const matchedBasePath =
    base === PUBLIC_LISTING_BASE_PATH || base === LEGACY_PUBLIC_LISTING_BASE_PATH ? base : null;

  if (!matchedBasePath) {
    return {
      matchedBasePath: null,
      isLegacyBasePath: false,
      isValid: false,
      seoSegments: emptySeoSegments(),
    };
  }

  const routeSegments = segments.slice(1, 6);
  if (segments.length > 6) {
    return {
      matchedBasePath,
      isLegacyBasePath: matchedBasePath === LEGACY_PUBLIC_LISTING_BASE_PATH,
      isValid: false,
      seoSegments: emptySeoSegments(),
    };
  }

  const propertyType = resolvePropertyTypeSegment(routeSegments[0]);
  if (routeSegments.length >= 1 && !propertyType) {
    return {
      matchedBasePath,
      isLegacyBasePath: matchedBasePath === LEGACY_PUBLIC_LISTING_BASE_PATH,
      isValid: false,
      seoSegments: emptySeoSegments(),
    };
  }

  const propertySubtype = resolvePropertySubtypeSegment(propertyType, routeSegments[1]);
  if (routeSegments.length >= 2 && !propertySubtype) {
    return {
      matchedBasePath,
      isLegacyBasePath: matchedBasePath === LEGACY_PUBLIC_LISTING_BASE_PATH,
      isValid: false,
      seoSegments: {
        propertyType,
        propertySubtype: null,
        city: null,
        municipality: null,
        colonia: null,
      },
    };
  }

  if (routeSegments.length >= 3 && !propertySubtype) {
    return {
      matchedBasePath,
      isLegacyBasePath: matchedBasePath === LEGACY_PUBLIC_LISTING_BASE_PATH,
      isValid: false,
      seoSegments: {
        propertyType,
        propertySubtype,
        city: null,
        municipality: null,
        colonia: null,
      },
    };
  }

  const city = routeSegments[2] ? humanizeListingSegment(routeSegments[2]) : null;
  const municipality = routeSegments[3] ? humanizeListingSegment(routeSegments[3]) : null;
  const colonia = routeSegments[4] ? humanizeListingSegment(routeSegments[4]) : null;

  if (routeSegments.length >= 4 && !city) {
    return {
      matchedBasePath,
      isLegacyBasePath: matchedBasePath === LEGACY_PUBLIC_LISTING_BASE_PATH,
      isValid: false,
      seoSegments: {
        propertyType,
        propertySubtype,
        city: null,
        municipality: null,
        colonia: null,
      },
    };
  }

  if (routeSegments.length >= 5 && !municipality) {
    return {
      matchedBasePath,
      isLegacyBasePath: matchedBasePath === LEGACY_PUBLIC_LISTING_BASE_PATH,
      isValid: false,
      seoSegments: {
        propertyType,
        propertySubtype,
        city,
        municipality: null,
        colonia: null,
      },
    };
  }

  return {
    matchedBasePath,
    isLegacyBasePath: matchedBasePath === LEGACY_PUBLIC_LISTING_BASE_PATH,
    isValid: true,
    seoSegments: {
      propertyType,
      propertySubtype,
      city,
      municipality,
      colonia,
    },
  };
}

function buildLocationLabelFromSeoSegments(seoSegments: PublicPropertiesSeoSegments): string | null {
  const parts = [seoSegments.colonia, seoSegments.municipality, seoSegments.city].filter(isNonEmptyString);
  return parts.length ? parts.join(', ') : null;
}

function resolveDerivedSearchText(filters: PublicPropertyFilters): string | null {
  if (isNonEmptyString(filters.searchText)) return filters.searchText.trim();
  if (filters.colonias?.length === 1) return filters.colonias[0];
  if (filters.alcaldias?.length === 1) return filters.alcaldias[0];
  if (filters.cities?.length === 1) return filters.cities[0];
  return null;
}

function resolveSeoSegmentsFromFilters(filters: PublicPropertyFilters): PublicPropertiesSeoSegments {
  const propertyType = filters.propertyTypes?.length === 1 ? filters.propertyTypes[0] as PropertyTypeCode : null;
  const propertySubtype =
    propertyType && filters.propertySubtypes?.length === 1 && isSubtypeAllowedForType(propertyType, filters.propertySubtypes[0])
      ? filters.propertySubtypes[0]
      : null;
  const city = propertySubtype && filters.cities?.length === 1 ? normalizeLocationLabel(filters.cities[0]) : null;
  const municipality = city && filters.alcaldias?.length === 1 ? normalizeLocationLabel(filters.alcaldias[0]) : null;
  const colonia = municipality && filters.colonias?.length === 1 ? normalizeLocationLabel(filters.colonias[0]) : null;

  return {
    propertyType,
    propertySubtype,
    city,
    municipality,
    colonia,
  };
}

function buildCanonicalListingPath(filters: PublicPropertyFilters): string {
  const seoSegments = resolveSeoSegmentsFromFilters(filters);
  const pathSegments = [
    seoSegments.propertyType ? slugifyListingSegment(seoSegments.propertyType) : null,
    seoSegments.propertySubtype ? slugifyListingSegment(seoSegments.propertySubtype) : null,
    seoSegments.city ? slugifyListingSegment(seoSegments.city) : null,
    seoSegments.municipality ? slugifyListingSegment(seoSegments.municipality) : null,
    seoSegments.colonia ? slugifyListingSegment(seoSegments.colonia) : null,
  ].filter((value): value is string => Boolean(value));

  return pathSegments.length ? `${PUBLIC_LISTING_BASE_PATH}/${pathSegments.join('/')}` : PUBLIC_LISTING_BASE_PATH;
}

function buildCanonicalQueryParams(options: BuildPublicPropertiesHrefOptions): URLSearchParams {
  const params = new URLSearchParams();
  const filters = normalizePublicPropertyFilters(options.filters);
  const seoSegments = resolveSeoSegmentsFromFilters(filters);
  const derivedSearchText = resolveDerivedSearchText(filters);
  const derivedLocationLabel = buildLocationLabelFromSeoSegments(seoSegments);
  const page = Math.max(options.page ?? 1, 1);

  if (page > 1) params.set('page', String(page));
  if (typeof filters.publicId === 'number') params.set('public_id', String(filters.publicId));
  if (filters.discounted === true) params.set('discounted', 'true');

  const propertyTypes = filters.propertyTypes?.filter(isNonEmptyString) ?? [];
  if (!seoSegments.propertyType) {
    if (propertyTypes.length === 1) params.set('property_type', propertyTypes[0]);
    if (propertyTypes.length > 1) params.set('property_types', JSON.stringify(propertyTypes));
  }

  const propertySubtypes = filters.propertySubtypes?.filter(isNonEmptyString) ?? [];
  if (!seoSegments.propertySubtype) {
    if (propertySubtypes.length === 1) params.set('property_subtype', propertySubtypes[0]);
    if (propertySubtypes.length > 1) params.set('property_subtypes', JSON.stringify(propertySubtypes));
  }

  const cities = filters.cities?.filter(isNonEmptyString) ?? [];
  if (!seoSegments.city) {
    if (cities.length === 1) params.set('city', cities[0]);
    if (cities.length > 1) params.set('cities', JSON.stringify(cities));
  }

  if (!seoSegments.municipality && filters.alcaldias?.length) {
    params.set('alcaldias', JSON.stringify(filters.alcaldias));
  }

  if (!seoSegments.colonia && filters.colonias?.length) {
    params.set('colonias', JSON.stringify(filters.colonias));
  }

  if (typeof filters.minPrice === 'number') params.set('min_price', String(filters.minPrice));
  if (typeof filters.maxPrice === 'number') params.set('max_price', String(filters.maxPrice));
  if (typeof filters.minArea === 'number') params.set('min_area', String(filters.minArea));
  if (typeof filters.maxArea === 'number') params.set('max_area', String(filters.maxArea));
  if (typeof filters.bedrooms === 'number') params.set('bedrooms', String(filters.bedrooms));
  if (typeof filters.minBathrooms === 'number') params.set('bathrooms', String(filters.minBathrooms));
  if (typeof filters.minHalfBathrooms === 'number') params.set('half_bathrooms', String(filters.minHalfBathrooms));
  if (typeof filters.minParkingSpots === 'number') params.set('parking', String(filters.minParkingSpots));

  if (filters.furnished === true) params.set('furnished', 'true');
  if (filters.semiFurnished === true) params.set('semi_furnished', 'true');
  if (filters.petsAllowed === true) params.set('pets_allowed', 'true');
  if (filters.withoutGuarantor === true) params.set('without_guarantor', 'true');

  if (filters.features?.length) params.set('features', JSON.stringify(filters.features));
  if (filters.amenities?.length) params.set('amenities', JSON.stringify(filters.amenities));
  if (filters.services?.length) params.set('services', JSON.stringify(filters.services));
  if (filters.internalCharacteristics?.length) {
    params.set('internal_characteristics', JSON.stringify(filters.internalCharacteristics));
  }
  if (filters.serviciosIncluidos?.length) {
    params.set('servicios_incluidos', JSON.stringify(filters.serviciosIncluidos));
  }

  const explicitLocationLabel = options.locationLabel?.trim() || null;
  if (explicitLocationLabel && explicitLocationLabel !== derivedLocationLabel) {
    params.set('location', explicitLocationLabel);
  }

  if (typeof options.lat === 'number') params.set('lat', String(options.lat));
  if (typeof options.lng === 'number') params.set('lng', String(options.lng));
  if (typeof filters.radiusMeters === 'number') params.set('radius_meters', String(filters.radiusMeters));

  if (isNonEmptyString(filters.searchText) && filters.searchText.trim() !== derivedSearchText) {
    params.set('q', filters.searchText.trim());
  }

  return params;
}

export function isPublicPropertiesListingPath(pathname: string): boolean {
  const cleanPath = (pathname || '').split('?')[0].split('#')[0];
  return cleanPath === PUBLIC_LISTING_BASE_PATH || cleanPath.startsWith(`${PUBLIC_LISTING_BASE_PATH}/`) || cleanPath === LEGACY_PUBLIC_LISTING_BASE_PATH;
}

export function buildPublicPropertiesHref(options: BuildPublicPropertiesHrefOptions): string {
  const filters = normalizePublicPropertyFilters(options.filters);
  const pathname = buildCanonicalListingPath(filters);
  const query = buildCanonicalQueryParams(options).toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function buildPublicPropertiesCanonicalUrl(options: BuildPublicPropertiesHrefOptions & { site: string }): string {
  const canonicalHref = buildPublicPropertiesHref({ ...options, page: 1, lat: null, lng: null });
  const url = new URL(canonicalHref, options.site);
  url.searchParams.delete('page');
  url.searchParams.delete('lat');
  url.searchParams.delete('lng');
  url.searchParams.delete('radius_meters');
  url.searchParams.delete('location');
  url.searchParams.delete('q');
  return url.toString();
}

export function parsePublicPropertiesUrlState(input: ParsePublicPropertiesInput): PublicPropertiesUrlState {
  const pathname =
    typeof input === 'string' || input instanceof URLSearchParams
      ? PUBLIC_LISTING_BASE_PATH
      : input.pathname ?? PUBLIC_LISTING_BASE_PATH;

  const params =
    input instanceof URLSearchParams
      ? new URLSearchParams(input)
      : typeof input === 'string'
        ? new URLSearchParams(input)
        : new URLSearchParams(input.search ?? '');

  const page = Math.max(parseInteger(params.get('page')) ?? 1, 1);
  const searchText = firstValue(params, ['q', 'query']);
  const publicId = parseInteger(firstValue(params, ['publicId', 'public_id']));
  const discounted = parseBoolean(firstValue(params, ['discounted', 'has_discount']));
  const withoutGuarantor = parseBoolean(firstValue(params, ['without_guarantor', 'sin_aval']));

  const propertyTypes = parseStringArrayParam(params, ['property_types'], ['type', 'property_type']);
  const propertySubtypes = parseStringArrayParam(params, ['property_subtypes'], ['subtype', 'property_subtype']);
  const cities = parseStringArrayParam(params, ['cities'], ['city']);
  const alcaldias = parseStringArrayParam(params, ['alcaldias']);
  const colonias = parseStringArrayParam(params, ['colonias']);
  const operationType = firstValue(params, ['op', 'operation_type']);

  const bedrooms = parseInteger(firstValue(params, ['bedrooms', 'bedrooms_min']));
  const bathrooms = parseInteger(firstValue(params, ['bathrooms', 'bathrooms_min']));
  const minHalfBathrooms = parseInteger(firstValue(params, ['half_bathrooms', 'half_bathrooms_min', 'min_half_bathrooms']));
  const parking = parseInteger(firstValue(params, ['parking', 'parking_min']));

  const minPrice = parseInteger(firstValue(params, ['min_price', 'price_min']));
  const maxPrice = parseInteger(firstValue(params, ['max_price', 'price_max']));
  const minArea = parseInteger(params.get('min_area'));
  const maxArea = parseInteger(params.get('max_area'));

  const lat = parseFloatNumber(params.get('lat'));
  const lng = parseFloatNumber(params.get('lng'));
  const radiusMeters = parseInteger(firstValue(params, ['radius_meters', 'radiusMeters']));
  const explicitLocationLabel = firstValue(params, ['location']);

  const pathState = stripListingBasePath(pathname);

  const filters = normalizePublicPropertyFilters({
    searchText,
    publicId,
    discounted,
    operationTypes: operationType?.toLowerCase() === RENT_ONLY_OPERATION ? [RENT_ONLY_OPERATION] : [RENT_ONLY_OPERATION],
    propertyTypes: pathState.seoSegments.propertyType ? [pathState.seoSegments.propertyType] : propertyTypes,
    propertySubtypes: pathState.seoSegments.propertySubtype ? [pathState.seoSegments.propertySubtype] : propertySubtypes,
    cities: pathState.seoSegments.city ? [pathState.seoSegments.city] : cities?.map(normalizeLocationLabel) ?? null,
    alcaldias: pathState.seoSegments.municipality ? [pathState.seoSegments.municipality] : alcaldias?.map(normalizeLocationLabel) ?? null,
    colonias: pathState.seoSegments.colonia ? [pathState.seoSegments.colonia] : colonias?.map(normalizeLocationLabel) ?? null,
    minPrice,
    maxPrice,
    minArea,
    maxArea,
    bedrooms,
    minBathrooms: bathrooms,
    minHalfBathrooms,
    minParkingSpots: parking,
    furnished: parseBoolean(params.get('furnished')),
    semiFurnished: parseBoolean(firstValue(params, ['semi_furnished', 'semiFurnished'])),
    petsAllowed: parseBoolean(params.get('pets_allowed')),
    withoutGuarantor,
    features: parseJsonArray(params.get('features')),
    amenities: parseJsonArray(params.get('amenities')),
    services: parseJsonArray(params.get('services')),
    internalCharacteristics: parseJsonArray(firstValue(params, ['internal_characteristics', 'internalCharacteristics'])),
    serviciosIncluidos: parseJsonArray(firstValue(params, ['servicios_incluidos', 'serviciosIncluidos'])),
    centerLat: lat,
    centerLng: lng,
    radiusMeters,
  });

  const locationLabel = explicitLocationLabel ?? buildLocationLabelFromSeoSegments(resolveSeoSegmentsFromFilters(filters));
  const canonicalHref = buildPublicPropertiesHref({
    filters,
    page,
    locationLabel,
    lat,
    lng,
  });
  const [canonicalPath, canonicalSearch = ''] = canonicalHref.split('?', 2);
  const currentPath = pathname || PUBLIC_LISTING_BASE_PATH;
  const currentSearch = params.toString();
  const currentHref = currentSearch ? `${currentPath}?${currentSearch}` : currentPath;

  return {
    filters,
    page,
    locationLabel,
    lat,
    lng,
    seoSegments: resolveSeoSegmentsFromFilters(filters),
    canonicalPath,
    canonicalSearch,
    canonicalHref,
    isCanonical: pathState.isValid && !pathState.isLegacyBasePath && currentHref === canonicalHref,
    matchedBasePath: pathState.matchedBasePath,
    isLegacyBasePath: pathState.isLegacyBasePath,
  };
}

export function serializePublicPropertiesUrlState(options: BuildPublicPropertiesHrefOptions): URLSearchParams {
  return buildCanonicalQueryParams(options);
}
